<script>
  import { format } from 'svelte-i18n';
  import { format as formatDate } from 'date-fns';

  import { Chart } from '@client/components/charts';

  import { CHART_TYPES } from '@client/enums';

  import { capitalize, truncate } from '@common/utils/strings';

  export let rows = {};
  export let label = null;
  export let title = null;
  export let subtitle = null;
  export let height = 'auto';
  export let width = 'auto';
  export let startDate = null;
  export let endDate = null;

  let tooltip =
    'This chart aggregates all document events for the current time period, and displays them grouped by event type.';

  let data = {};

  let options = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (tooltipItem) {
            if (tooltipItem.raw === 0) {
              return null;
            }

            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
          }
        }
      }
    },
    helpers: {
      tooltipContentFormatter(content, lines, tooltip) {
        const groups = {};
        const values = [];

        lines.forEach((body, i) => {
          if (body.length) {
            const [filename, type, value] = body[0].split(/( - \(\w+\):)/);
            const metric = type.replace(/[():-\s]/g, '');
            const filetype = filename.split('.').pop();
            const color = tooltip.labelColors[i];
            const name = metric;
            const val = Number(value);

            values.push(val);

            if (!groups[name]) {
              groups[name] = [];
            }

            groups[name].push({
              filename: `${truncate(filename, 30, filetype)}`,
              backgroundColor: typeof color.backgroundColor === 'string' ? color.backgroundColor : color.borderColor,
              borderColor: color.borderColor,
              value: val
            });
          }
        });

        const isEmptySet = !values.filter(Boolean).length;

        if (isEmptySet) {
          return false;
        }

        for (const group in groups) {
          const items = groups[group];
          const name = document.createElement('div');
          const sorted = items.sort((a, b) => a.value - b.value);
          const hasValues = sorted.map(({ value }) => value).filter(Boolean).length;

          if (!hasValues) {
            continue;
          }

          name.innerHTML = capitalize(group);
          name.style.margin = '8px 0';
          name.style.fontWeight = '500';

          content.appendChild(name);

          for (let i = 0, k = sorted.length; i < k; i++) {
            const config = sorted[i];

            if (config.value === 0) {
              continue;
            }

            const point = document.createElement('div');

            point.className = 'point';
            point.style.background = config.backgroundColor;
            point.style.borderColor = config.borderColor;

            const item = document.createElement('div');

            item.className = 'item';

            const metric = document.createElement('div');
            const value = document.createElement('div');

            metric.className = 'metric';
            metric.innerHTML = config.filename;

            value.className = 'value';
            value.innerHTML = config.value;

            const pair = document.createElement('div');

            pair.className = 'pair';

            pair.appendChild(metric);
            pair.appendChild(value);

            item.appendChild(point);
            item.appendChild(pair);

            content.appendChild(item);
          }
        }

        return content;
      }
    },
    scales: {
      x: {
        type: 'time',
        position: 'bottom',
        time: {
          parser: 'yyyy-MM-dd',
          displayFormats: {
            day: 'MMM d',
            month: 'MMM yy'
          },
          tooltipFormat: 'MMM d, yyyy',
          unit: 'day'
        },
        ticks: {
          maxRotation: 0
        }
      },
      y: {
        min: 0,
        ticks: {
          beginAtZero: true,
          callback: (value) => (Number.isInteger(value) ? value : ''),
          stepSize: 2,
          backdropPadding: {
            y: 8
          }
        }
      }
    }
  };

  const generateDataset = () => {
    const timeline = {};
    const filenames = new Set();
    const today = new Date();

    rows.forEach((item) => {
      item.activity.forEach((activity) => {
        const date = formatDate(new Date(activity.utcts), 'yyyy-MM-dd');
        const filename = activity.note?.filename;

        if (filename) {
          filenames.add(filename);

          if (!endDate) {
            endDate = today;
          }

          if (!startDate) {
            startDate = new Date(today).setDate(new Date(today).getDate() - 59);
          }

          if (!timeline[date]) {
            timeline[date] = {};
          }

          if (!timeline[date][filename]) {
            timeline[date][filename] = {
              viewed: 0,
              downloaded: 0
            };
          }

          if (activity.verb === 'viewerlaunched') {
            timeline[date][filename].viewed++;
          }

          if (activity.verb === 'downloaded') {
            timeline[date][filename].downloaded++;
          }
        }
      });
    });

    const dateRange = [];
    let currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      const dateStr = currentDate.getTime();
      dateRange.push(formatDate(dateStr, 'yyyy-MM-dd'));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const datasets = [];

    filenames.forEach((file) => {
      const viewedData = dateRange.map((date) => timeline[date]?.[file]?.viewed || 0);
      const downloadedData = dateRange.map((date) => timeline[date]?.[file]?.downloaded || 0);

      datasets.push({
        label: `${file} - (${$format('label.VIEWED')})`,
        data: viewedData,
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.3
      });

      datasets.push({
        label: `${file} - (${$format('label.DOWNLOADED')})`,
        data: downloadedData,
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.3
      });
    });

    return { labels: dateRange, datasets };
  };

  $: data = generateDataset(rows);
</script>

<Chart
  type={CHART_TYPES.line.id}
  bind:data
  bind:options
  bind:label
  bind:title
  bind:subtitle
  bind:height
  bind:width
  bind:tooltip
/>
